import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2023 semantics GmbH
//  Date      : $Date: 2026-10-17 16:46:05 +0200 (Sat, 17 Oct 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: o.juenger $
//  Revision  : $Rev: 166681 $
//  Purpose   : showcase components
// =============================================================================
// ---Third-party imports
import React from 'react';
import clsx from 'clsx';
// ---Framework imports
import log from 'uix/logging';
import { useApi, useSelector, useState, useCallback, useRef, useEffect, useInView, useRouter } from 'uix/hooks';
import { Sel } from 'uix/registries';
import { Button } from 'uix/components';
import { GhostImage, GhostText, PageViewer, Modal } from '.';
import { VIcon } from 'uix/graphics';
import { useWikiContext } from 'uix/wiki/utils';
// ---Local imports
import S from './showcase.css';
import { useCaptionLink, useModalWidSync } from '../hooks';
import { XPopup, AccessWarning } from 'uix/components';
import { PopupContent, PopupHeader } from 'semantic-ui-react';
/// ---Exports
const useImageInfo = (id)=>{
    let w = 1000;
    let imgsrc = `/download/webcache/${w}/${id}`;
    let [isError, setIsError] = useState(false);
    let onError = useCallback(()=>setIsError(true), []);
    return {
        imgsrc,
        onError,
        isError
    };
};
export const Showcase = ({ otid, otids, caption, fontSize, linkid, carousel, imagePosition, imageWidth = '504px', limitText, showFulltext, optionSet, skipFirstPage })=>{
    let apiurl = useSelector(Sel.apiurl);
    let i18n = useSelector(Sel.i18n);
    let { mobile } = useWikiContext();
    let [outerRef, inView] = useInView({
        triggerOnce: true,
        rootMargin: '100%'
    });
    let imgRef = useRef(null);
    let contentRef = useRef(null);
    let [hidden, setHidden] = useState(false);
    let [txtOverflow, setTxtOverflow] = useState(false);
    let [imgHeight, setImgHeight] = useState(false);
    let [imgLoad, setImgLoad] = useState(false);
    let [isFirstLoad, setIsFirstLoad] = useState(true);
    let [currentIndex, setCurrentIndex] = useState(0);
    let [centeredOtid, setCenteredOtid] = useState(undefined) // centered Otid in Modal
    ;
    let [modalOpen, setModalOpen] = useState(false);
    let [popupOpen, setPopupOpen] = useState(false);
    useEffect(()=>{
        const measure = ()=>{
            let imgHeight = imgRef.current?.clientHeight || 0;
            setHidden(false);
            setImgHeight(imgHeight);
            setTxtOverflow(imgHeight < (contentRef.current?.clientHeight || 0) - 5);
        };
        measure();
        let rObserver = new ResizeObserver(measure);
        if (imgRef.current) rObserver.observe(imgRef.current);
        return ()=>rObserver.disconnect();
    }, [
        imgLoad,
        currentIndex
    ]);
    let { request } = useRouter();
    let wid = request.query.wid;
    let visible = !!otid && (inView || !!wid);
    let { data, loading: apiLoading } = useApi('presentation/getShowcaseData', {
        otid,
        resolveChildren: !!carousel
    }, {
        condition: visible
    });
    let loading = !data || apiLoading && isFirstLoad;
    const flattenPages = (node, parentText = '', parentTitle = '')=>{
        // bring children and pages on one level
        let currentLevel = [];
        if (node.pages) {
            currentLevel.push(...node.pages.map((p)=>({
                    ...p,
                    text: p.text || node.text || parentText,
                    title: p.title || node.title || parentTitle
                })));
        }
        if (node.children) {
            currentLevel.push(...node.children.map((c)=>({
                    ...c,
                    text: c.text || node.text || parentText,
                    title: c.title || node.title || parentTitle,
                    _isNode: true
                })));
        }
        // sort on first level
        currentLevel.sort((a, b)=>(a.otsort || 0) - (b.otsort || 0));
        // recursion on children (puts parent item.text & .info into children)
        let flattened = [];
        currentLevel.forEach((item)=>{
            if (item._isNode) {
                flattened = flattened.concat(flattenPages(item, item.text, item.title));
            } else {
                flattened.push(item);
            }
        });
        return flattened;
    };
    let allPages = carousel ? data ? flattenPages(data) : [] : data?.pages?.[0] ? [
        {
            ...data.pages[0],
            text: data.text,
            awstate: data.awstate
        }
    ] : [];
    let totalSlides = allPages.length;
    let { imgsrc, onError } = useImageInfo(allPages[currentIndex]?.otid);
    let getIdxByOtid = (otid)=>{
        const idx = allPages.findIndex((p)=>String(p.otid) === String(otid));
        if (idx !== -1) return idx;
    };
    let widIdx = wid ? getIdxByOtid(wid) : undefined;
    useEffect(()=>{
        // deep-link: jump carousel to the page matching ?wid before the modal-sync hook opens it
        if (!modalOpen && widIdx !== undefined && widIdx !== currentIndex) setCurrentIndex(widIdx);
    }, [
        wid,
        widIdx
    ]);
    let { openModal, closeModal } = useModalWidSync(allPages[currentIndex]?.otid, modalOpen, setModalOpen);
    let onGraphicClick = openModal;
    let nextSlide = ()=>setCurrentIndex((prev)=>(prev + 1) % totalSlides);
    let prevSlide = ()=>setCurrentIndex((prev)=>(prev - 1 + totalSlides) % totalSlides);
    let goFirst = ()=>{
        setImgLoad(false);
        setCurrentIndex(0);
    };
    let goLast = ()=>{
        setImgLoad(false);
        setCurrentIndex(totalSlides - 1);
    };
    let onKeyActivate = (handler)=>(e)=>{
            if ((e.key === 'Enter' || e.key === ' ') && handler) {
                e.preventDefault();
                handler(e);
            }
        };
    let atStart = currentIndex <= 0;
    let atEnd = currentIndex >= totalSlides - 1;
    let img = /*#__PURE__*/ _jsx(AccessWarning, {
        id: currentIndex,
        awstate: allPages[currentIndex]?.awstate,
        children: /*#__PURE__*/ _jsx("img", {
            onLoad: ()=>{
                setImgLoad(true);
                setIsFirstLoad(false);
            },
            onClick: onGraphicClick,
            src: apiurl.href(imgsrc),
            onError: onError,
            ref: imgRef,
            loading: "lazy"
        })
    });
    let textStyle = null;
    if (!hidden && !loading) {
        textStyle = {
            maxHeight: !limitText ? 'none' : `${imgHeight - 5}px`,
            opacity: imgHeight ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out'
        };
    }
    let buildMore = ()=>{
        if (!limitText) return null;
        let handleMore = ()=>setHidden(!hidden);
        if (!hidden) {
            return /*#__PURE__*/ _jsxs("a", {
                role: "button",
                tabIndex: 0,
                "aria-expanded": !!hidden,
                "aria-label": i18n.get('pres_a11y.more_show', 'Mehr anzeigen'),
                onClick: handleMore,
                onKeyDown: onKeyActivate(handleMore),
                className: S.more,
                children: [
                    "mehr ",
                    /*#__PURE__*/ _jsx(VIcon, {
                        size: "small",
                        name: "arrow down"
                    })
                ]
            });
        }
    };
    let modalClose = ()=>{
        setCurrentIndex(getIdxByOtid(centeredOtid));
        closeModal();
    };
    let centeredInfo = null;
    if (centeredOtid) {
        centeredInfo = allPages.find((p)=>p.otid === centeredOtid);
    }
    let infoLabel = i18n.get('pres_a11y.info', 'Informationen anzeigen');
    let popupId = `showcase-info-popup-${otid}`;
    const addInfoPopup = /*#__PURE__*/ _jsxs(XPopup, {
        basic: true,
        style: {
            opacity: 0.86,
            maxWidth: '66%',
            marginBottom: '2em'
        },
        wide: "very",
        inverted: true,
        size: "small",
        open: popupOpen,
        // onOpen={() => setPopupOpen(true)}
        // positionFixed
        position: "right center",
        role: "dialog",
        id: popupId,
        "aria-label": centeredInfo?.title || infoLabel,
        trigger: /*#__PURE__*/ _jsx(Button, {
            className: S.infoButton,
            onClick: ()=>setPopupOpen(!popupOpen),
            icon: popupOpen ? 'close' : 'info',
            ariaLabel: infoLabel,
            ariaExpanded: popupOpen,
            ariaHasPopup: "dialog",
            ariaControls: popupOpen ? popupId : undefined
        }),
        children: [
            /*#__PURE__*/ _jsxs(PopupHeader, {
                children: [
                    centeredInfo?.title,
                    ' ',
                    /*#__PURE__*/ _jsx(VIcon, {
                        style: {
                            display: 'inline-block'
                        },
                        name: "link",
                        href: apiurl.baseUrl('/' + centeredOtid),
                        target: "_blank",
                        rel: "noopener noreferrer",
                        "aria-label": i18n.get('pres_a11y.openSource', 'Link zur Quelle'),
                        title: i18n.get('pres_a11y.openSource', 'Link zur Quelle')
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(PopupContent, {
                children: centeredInfo?.text
            })
        ]
    });
    let options = optionSet || showFulltext && 'h1' || 'grid';
    let modalContent = /*#__PURE__*/ _jsx(PageViewer, {
        otids: allPages.map((pages)=>pages.otid),
        otid: [
            allPages[currentIndex]?.otid
        ],
        onCenterChange: (otid)=>setCenteredOtid(otid),
        mobile: mobile,
        optionSet: options,
        navigationControls: true,
        height: "100%",
        zoomControls: "inOut",
        initialZoomMode: "areaFit",
        skipFirstPage: skipFirstPage,
        showFulltext: showFulltext,
        addContent: (area)=>{
            if (!centeredInfo) return null;
            if (area === 'viewer') {
                return /*#__PURE__*/ _jsx("span", {
                    style: {
                        color: 'red',
                        position: 'absolute',
                        bottom: '.3rem',
                        left: '.3rem',
                        zIndex: 20
                    },
                    children: addInfoPopup
                });
            }
        }
    });
    let captionLink = useCaptionLink(caption, linkid, otid, null, true);
    return /*#__PURE__*/ _jsxs("div", {
        className: clsx(S.showcase, imagePosition === 'right' && S.imageRight),
        ref: outerRef,
        "data-modal-refid": widIdx !== undefined ? wid : undefined,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: clsx(S.content, loading && S.loading),
                children: [
                    carousel && /*#__PURE__*/ _jsxs("div", {
                        className: clsx(S.carouselNav, totalSlides > 1 && S.withSlides),
                        children: [
                            /*#__PURE__*/ _jsx("a", {
                                role: "button",
                                tabIndex: atStart ? -1 : 0,
                                "aria-disabled": atStart || undefined,
                                "aria-label": i18n.get('pres_a11y.toStart', 'Zum Anfang der Webaustellung'),
                                className: clsx(S.prev, atStart && S.disabled),
                                onClick: !atStart ? goFirst : undefined,
                                onKeyDown: onKeyActivate(!atStart ? goFirst : undefined),
                                children: /*#__PURE__*/ _jsx(VIcon, {
                                    name: "chevron_left_end"
                                })
                            }),
                            /*#__PURE__*/ _jsx("a", {
                                role: "button",
                                tabIndex: atStart ? -1 : 0,
                                "aria-disabled": atStart || undefined,
                                "aria-label": i18n.get('pres_a11y.previous', 'Zurück'),
                                className: clsx(S.prev, atStart && S.disabled),
                                onClick: !atStart ? prevSlide : undefined,
                                onKeyDown: onKeyActivate(!atStart ? prevSlide : undefined),
                                children: /*#__PURE__*/ _jsx(VIcon, {
                                    name: "chevron_left"
                                })
                            }),
                            /*#__PURE__*/ _jsxs("span", {
                                "aria-hidden": "true",
                                children: [
                                    currentIndex + 1,
                                    " / ",
                                    totalSlides
                                ]
                            }),
                            /*#__PURE__*/ _jsx("a", {
                                role: "button",
                                tabIndex: atEnd ? -1 : 0,
                                "aria-disabled": atEnd || undefined,
                                "aria-label": i18n.get('pres_a11y.next', 'Weiter'),
                                className: clsx(S.next, atEnd && S.disabled),
                                onClick: !atEnd ? nextSlide : undefined,
                                onKeyDown: onKeyActivate(!atEnd ? nextSlide : undefined),
                                children: /*#__PURE__*/ _jsx(VIcon, {
                                    name: "chevron_right"
                                })
                            }),
                            /*#__PURE__*/ _jsx("a", {
                                role: "button",
                                tabIndex: atEnd ? -1 : 0,
                                "aria-disabled": atEnd || undefined,
                                "aria-label": i18n.get('pres_a11y.toEnd', 'Zum Ende der Webaustellung'),
                                className: clsx(S.next, atEnd && S.disabled),
                                onClick: !atEnd ? goLast : undefined,
                                onKeyDown: onKeyActivate(!atEnd ? goLast : undefined),
                                children: /*#__PURE__*/ _jsx(VIcon, {
                                    name: "chevron_right_end"
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: clsx(S.imgContainer),
                        style: {
                            '--presenation_showcase_imageWidth': imageWidth
                        },
                        children: [
                            loading && /*#__PURE__*/ _jsx(GhostImage, {
                                width: "100%",
                                height: imgHeight || '320px'
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                style: {
                                    visibility: loading ? 'hidden' : 'visible'
                                },
                                children: allPages[currentIndex]?.otid && /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx("div", {
                                            className: S.clickable
                                        }),
                                        /*#__PURE__*/ _jsx(Button, {
                                            size: "large",
                                            onClick: onGraphicClick,
                                            className: S.zoomButton,
                                            icon: 'zoom',
                                            ariaLabel: i18n.get('pres_a11y.zoom_more', 'Details erkunden'),
                                            circle: true,
                                            inverted: true
                                        }),
                                        img,
                                        captionLink
                                    ]
                                })
                            })
                        ]
                    }),
                    loading ? /*#__PURE__*/ _jsx(GhostText, {
                        style: {
                            marginLeft: '20px',
                            width: 'inherit'
                        }
                    }) : /*#__PURE__*/ _jsx("div", {
                        style: textStyle,
                        className: clsx(S.textArea, limitText && txtOverflow && !hidden && S.hidden),
                        children: /*#__PURE__*/ _jsx("div", {
                            ref: contentRef,
                            style: {
                                fontSize: fontSize
                            },
                            children: allPages[currentIndex]?.text.split('\n').map((line, index)=>/*#__PURE__*/ _jsx("div", {
                                    style: {
                                        minHeight: '1em'
                                    },
                                    children: line
                                }, index))
                        }, currentIndex)
                    }),
                    !loading && /*#__PURE__*/ _jsx("div", {
                        className: S.actions,
                        children: txtOverflow && buildMore()
                    })
                ]
            }),
            modalOpen && /*#__PURE__*/ _jsx(Modal, {
                fullwidth: true,
                open: modalOpen,
                onClose: modalClose,
                imageId: allPages[currentIndex]?.otid,
                children: modalContent
            })
        ]
    });
};
